/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useWebSocketContext } from '../WebSocket/WebsocketContext';
import {
  getUserInfo,
  getProfilePics,
  getUserHubs,
} from '@vantiva-sa/homesight_humaui/Store/dataStore';
import {
  handleVideoCallAccept,
  handleVideoCallReject,
  handleVideoCallTimeout,
} from '../Helpers/VideoCallHelper';
import useUserInfo from '../Hooks/useUserInfo';
import {
  formatFullNameDisplay,
  getProfilePicUrl,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  PhotoResolution,
  VideoCallNotificationTimeout,
  VideoCallStatusUpdate,
  WSMessageTypes,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { CallNewIcon, ContactImage, NewCallDeclineIcon } from '../Common/Icons';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { updateNotificationMiniBanner } from '@vantiva-sa/homesight_humaui/Store/notificationStore';
import calling from '../../../static/images/calling.gif';
import ringtone from '../../../static/audio/ringtone.mp3';
import {
  showIncomingCallNotification,
  showMissedCallNotification,
} from '../Helpers/DesktopNotificationHelper';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';

const audio = new Audio(ringtone);
audio.loop = true;

const VideoCallNotificationModal = () => {
  const dispatch = useDispatch();
  const trans = useSelector(translator);
  const { sendMessage } = useWebSocketContext();
  const resourcesMap = useSelector(getProfilePics());
  const notification = useSelector((state) => state.notification.videoCallNotification);
  const visible = useSelector((state) => state.notification.videoCallNotificationModalVisible);
  const caller = useSelector(getUserInfo(notification?.useruuid));
  const { isFetching: fetchingUserInfo } = useUserInfo({
    useruuid: notification?.useruuid,
  });
  const image = useMemo(
    () => getProfilePicUrl(caller?.useruuid, resourcesMap, PhotoResolution.R128),
    [caller?.useruuid, resourcesMap]
  );
  const meeting_id = useMemo(() => notification?.meeting_id, [notification]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const currentLoggedInUserConfig = useSelector((state) => state.data.userConfig);
  const currentUser = useSelector(getCurrentUser);
  const { useruuid } = useMemo(() => currentUser, [currentUser]);
  const [videoCallingNotificationTimeout, setVideoCallingNotificationTimeout] = useState(null);
  const hasLoggedAutoplayBlockRef = useRef(false);

  const userHubs = useSelector(getUserHubs(currentUser?.useruuid));
  const userHubsMapper = useMemo(() => {
    return userHubs.reduce((result, hub) => {
      result[hub.useruuid] = hub;
      return result;
    }, {});
  }, [userHubs]);
  useEffect(() => {
    const currentUserCallTimeout =
      currentLoggedInUserConfig[useruuid]?.huddle_app_env?.call_timeout;
    setVideoCallingNotificationTimeout(
      Number(currentUserCallTimeout) ?? VideoCallNotificationTimeout
    );
  }, [currentLoggedInUserConfig]);

  useEffect(() => {
    let timer = null;

    const playNotificationToneSafely = () => {
      const playPromise = audio?.play?.();
      if (!playPromise || typeof playPromise.catch !== 'function') {
        return;
      }

      playPromise.catch((error) => {
        if (error?.name === 'NotAllowedError') {
          if (!hasLoggedAutoplayBlockRef.current) {
            hasLoggedAutoplayBlockRef.current = true;
            console.warn(
              'Incoming call ringtone autoplay was blocked until user interaction. Notification flow continues.'
            );
          }
          return;
        }
        console.error('Calling Tune Error:', error);
      });
    };

    if (visible) {
      try {
        playNotificationToneSafely();
        timer = setTimeout(() => {
          handleVideoCallTimeout({ sendMessage, meeting_id, dispatch });
          dispatch(
            updateNotificationMiniBanner({
              message_type: WSMessageTypes.videoCallStatus,
              message: {
                meeting_id: meeting_id,
                call_status__status: VideoCallStatusUpdate.missed,
                event_originator_id: notification.useruuid,
                timestamp: new Date().toISOString(),
              },
            })
          );
          showMissedCallNotification({
            meeting_id: notification?.meeting_id,
            useruuid: notification?.useruuid,
          });
        }, videoCallingNotificationTimeout * 1000);
      } catch (error) {
        console.error('Calling Tune Error:', error);
      }
    }

    return () => {
      audio.pause();
      clearTimeout(timer);
    };
  }, [visible, caller]);

  useEffect(() => {
    if (visible && !fetchingUserInfo) {
      showIncomingCallNotification({
        meeting_id: notification?.meeting_id,
        useruuid: notification?.useruuid,
        videoCallingNotificationTimeout: videoCallingNotificationTimeout,
      });
    }
  }, [visible, caller, fetchingUserInfo]);

  const handleAccept = () => {
    handleVideoCallAccept({ sendMessage, meeting_id, dispatch });
  };

  const handleReject = () => {
    handleVideoCallReject({ sendMessage, meeting_id, dispatch });
    setTimeout(() => {
      dispatch(
        updateNotificationMiniBanner({
          message_type: WSMessageTypes.videoCallStatus,
          message: {
            call_status__status: VideoCallStatusUpdate.declined,
            meeting_id: meeting_id,
            event_originator_id: notification.useruuid,
            timestamp: new Date().toISOString(),
          },
        })
      );
    }, 500);
  };
  return (
    <>
      {calling && (
        <Modal
          open={visible}
          closable={false}
          footer={null}
          maskClosable={true}
          width={null}
          wrapClassName="vertical-center-modal video-call-notification-modal"
        >
          <div className="video-call-incoming-label-container">
            <span className="video-call-incoming-label">{trans('INCOMINGCALL')}</span>
          </div>
          <div className="video-incoming-call-content-block">
            <div className="image-container">
              {!imageLoaded && <ContactImage className="placeholder-profile-pic" />}
              {image && (
                <img
                  src={image}
                  className={`user-profile-pic ${imageLoaded ? 'show' : 'hide'}`}
                  alt="pic"
                  onLoad={() => setImageLoaded(true)}
                />
              )}
              {calling && <img className="calling-gif" src={calling} alt="" />}
            </div>
            <div className="video-incoming-call-name-relationship-container">
              <div className="video-incoming-call-name-container">
                <span className="video-incoming-call-name-label">
                  {caller && formatFullNameDisplay(caller?.firstname, caller?.lastname)}
                </span>
              </div>
              <div className="video-incoming-call-relationship-container">
                <span className="video-incoming-call-relationship-label">
                  {userHubsMapper[caller?.useruuid]?.relationship || caller?.relationship || ' '}
                </span>
              </div>
            </div>
          </div>
          <div className="video-incoming-call-footer-container">
            <div className="video-incoming-call-decline-button" onClick={handleReject}>
              <div className="video-incoming-call-footer-button-container">
                <NewCallDeclineIcon className="incoming-call-footer-icon" />
                <div className="incoming-call-footer-button-block">
                  <span className="incoming-call-decline-button-label">{trans('DECLINE')}</span>
                </div>
              </div>
            </div>

            <div className="video-incoming-call-answer-button" onClick={handleAccept}>
              <div className="video-incoming-call-footer-button-container">
                <CallNewIcon
                  className="incoming-call-footer-icon"
                  style={{ width: '24px', height: '24px' }}
                />
                <div className="incoming-call-footer-button-block">
                  <span className="incoming-call-answer-button-label">{trans('ANSWER')}</span>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

VideoCallNotificationModal.propTypes = {};

export default VideoCallNotificationModal;
